import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";

export default function AuthModal({ open, onOpenChange, title = "Start your track record" }) {
  const { loginWithGoogle, requestEmailCode, verifyEmailCode } = useAuth();
  const [step, setStep] = useState("choose");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const res = await requestEmailCode(email);
      if (res.dev_code) {
        setStep("code");
        toast.info(`Dev code: ${res.dev_code}`, { duration: 8000 });
      } else if (res.sent) {
        setStep("code");
        toast.success("Check your email for a login code");
      } else {
        toast.error("Couldn't send the email right now. Try Google sign-in, or another address.");
      }
    } catch {
      toast.error("Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await verifyEmailCode(email, code.trim());
      toast.success("You're in!");
      onOpenChange(false);
      setStep("choose");
    } catch {
      toast.error("Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="card-brutal border-2 border-ink sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        {step === "choose" && (
          <div className="space-y-3 pt-2">
            <Button
              data-testid="google-signin-btn"
              onClick={loginWithGoogle}
              className="w-full border-2 border-ink bg-white text-ink hover:bg-secondary font-semibold btn-press shadow-brutal-sm h-12"
            >
              <img alt="" src="https://www.google.com/favicon.ico" className="w-4 h-4 mr-2" />
              Continue with Google
            </Button>
            <button
              data-testid="email-signin-toggle"
              onClick={() => setStep("email")}
              className="w-full border-2 border-ink bg-brand text-white hover:brightness-110 font-semibold btn-press shadow-brutal-sm h-12 rounded-md flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" /> Continue with Email
            </button>
            <p className="text-xs text-center text-muted-foreground pt-1">
              No passwords. We use a one-time login code.
            </p>
          </div>
        )}
        {step === "email" && (
          <div className="space-y-3 pt-2">
            <Input
              data-testid="email-input" type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="border-2 border-ink h-12"
            />
            <Button data-testid="send-code-btn" disabled={busy} onClick={sendCode}
              className="w-full border-2 border-ink bg-ink text-white font-semibold btn-press shadow-brutal-sm h-12">
              Send login code <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <button className="text-xs text-muted-foreground underline w-full" onClick={() => setStep("choose")}>Back</button>
          </div>
        )}
        {step === "code" && (
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to <b>{email}</b></p>
            <Input
              data-testid="code-input" placeholder="123456" value={code}
              onChange={(e) => setCode(e.target.value)}
              className="border-2 border-ink h-12 font-mono text-xl tracking-widest text-center"
            />
            <Button data-testid="verify-code-btn" disabled={busy} onClick={verify}
              className="w-full border-2 border-ink bg-brand text-white font-semibold btn-press shadow-brutal-sm h-12">
              Verify & sign in
            </Button>
            <button className="text-xs text-muted-foreground underline w-full" onClick={() => setStep("email")}>Use a different email</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
